import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SpinnerService } from 'src/app/services/spinner/spinner.service';
import { SendInviteResponseData, TeamsService } from 'src/app/services/teams/teams.service';
import { SnackBarComponent } from '../snackbar/snackbar.component';
import {
  SendInvitesScheduleDialogComponent,
  SendInvitesScheduleDialogData,
  SendInvitesScheduleDialogResult,
} from './send-invites-schedule-dialog.component';

@Component({
    selector: 'app-send-invites-button',
    templateUrl: './send-invites-button.component.html',
    styleUrls: ['./send-invites-button.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SendInvitesButtonComponent {
  @Input() teamId!: string;
  @Input() disabled = false;
  @Input() pendingInvitesCount = 0;
  @Input() hasPendingAutomation = false;
  @Output() invitesSent = new EventEmitter<SendInviteResponseData>();

  constructor(
    private teamsService: TeamsService,
    private snackBar: MatSnackBar,
    private spinnerService: SpinnerService,
    private dialog: MatDialog,
  ) {}

  get buttonLabel(): string {
    return this.pendingInvitesCount > 0
      ? `Send ${this.pendingInvitesCount} ${this.pendingInvitesCount === 1 ? 'Invite' : 'Invites'}`
      : 'Send Invites';
  }

  get isButtonDisabled(): boolean {
    return this.disabled || (this.pendingInvitesCount === 0 && !this.hasPendingAutomation);
  }

  private mountMessage(result: SendInviteResponseData): string {
    const parts: string[] = [];
    if (result.sentCount > 0) {
      parts.push(`${result.sentCount} ${result.sentCount === 1 ? 'invite' : 'invites'} sent.`);
    } else if (result.attemptedCount === 0) {
      parts.push('No pending invites to send.');
    } else {
      parts.push('No invites were sent.');
    }

    if (result.failedCount > 0) {
      parts.push(`${result.failedCount} ${result.failedCount === 1 ? 'invite' : 'invites'} failed.`);
    }

    if (result.warning) {
      parts.push(result.warning);
    }

    return parts.join(' ');
  }

  public async send() {
    const ref = this.dialog.open<
      SendInvitesScheduleDialogComponent,
      SendInvitesScheduleDialogData,
      SendInvitesScheduleDialogResult
    >(SendInvitesScheduleDialogComponent, {
      data: { teamId: this.teamId },
      width: '520px',
      maxWidth: '95vw',
      autoFocus: false,
    });

    ref.afterClosed().subscribe((result) => {
      if (result?.invitesSent) {
        this.invitesSent.emit(result.invitesSent);
      }
    });
  }

  public async sendImmediately() {
    try {
      this.spinnerService.show('Sending invites...');
      const response = await this.teamsService.sendInvites(this.teamId);
      if (response.success) {
        this.snackBar.openFromComponent(SnackBarComponent, {
          data: this.mountMessage(response.data),
        });
        this.invitesSent.emit(response.data);
      } else {
        throw new Error(`${response.error}`);
      }
    } catch (error: any) {
      this.snackBar.openFromComponent(SnackBarComponent, {
        data: `Error sending invites: ${error.message || error.toString()}`,
      });
    }
    this.spinnerService.hide();
  }
}
